import { InformationProvider } from '@/providers/information/information.provider';
import { RosaCoreApiService } from '@/rosa';
import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { ClassificationId, ClassificationNodeId, ClassificationNodeType } from '../../../../interfaces/Classification.interface';
import type { AppUserFavoriteClassificationId, UserFavoriteClassification } from '../favorite-perimeter.interface';

@Injectable({
    providedIn: 'root'
})
export class FavoritePerimeterService {
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);
    private _rosaCoreApiService = inject(RosaCoreApiService);

    private reload$ = new BehaviorSubject<void>(undefined);

    getFavoriteActivities$(): Observable<UserFavoriteClassification[]> {
        return this.reload$.pipe(switchMap(() => this.fetchFavoriteActivities$()));
    }

    async deleteFavoritePerimeter$(favoriteId: AppUserFavoriteClassificationId): Promise<void> {
        await firstValueFrom(
            this._rosaCoreApiService.checkedDelete('/api/v3/favorite-classification/{favoriteId}', { path: { favoriteId: favoriteId } })
        );
        this.reload$.next();
    }

    addFavoritePerimeter$(
        classificationId: ClassificationId | null,
        classificationNodeId: ClassificationNodeId,
        classificationNodeType: ClassificationNodeType
    ): void {
        firstValueFrom(
            this._rosaCoreApiService.checkedPost('/api/v3/favorite-classification', {
                body: {
                    classificationId: classificationId,
                    classificationNodeId: classificationNodeId,
                    classificationNodeType: classificationNodeType
                }
            })
        )
            .then(() => {
                this._informationProvider.showToast(
                    this._translateService.instant('COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.CREATION.SUCCESS'),
                    'success'
                );
            })
            .then(() => {
                this.reload$.next();
            })
            .catch((httpErrorResponse: HttpErrorResponse) => {
                this._informationProvider.showToast(
                    httpErrorResponse?.error?.error?.errorCode === 'alreadyCreatedUserFavoriteClassification'
                        ? this._translateService.instant(
                              'COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.CREATION.ERROR_DUPLICATE_FAVORITE_PERIMETER'
                          )
                        : this._translateService.instant('COMPONENTS.MAIN_CLASSIFICATION_NODE_SELECTOR.CREATION.ERROR'),
                    'error'
                );
            });
    }

    private fetchFavoriteActivities$(): Observable<UserFavoriteClassification[]> {
        return this._rosaCoreApiService.checkedGet('/api/v3/favorite-classification/me', {}).pipe(
            map(responses =>
                responses.map(response => ({
                    ...response,
                    classificationNodeType: response.classificationNodeType as ClassificationNodeType
                }))
            )
        );
    }
}
